import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, IconButton, Tooltip, CircularProgress, Divider,
  Accordion, AccordionSummary, AccordionDetails, Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTranslation } from 'react-i18next';
import { fetchAttractionWeather, type AttractionWeather } from '../services/attractionService';
import { getWmoInfo } from '../utils/wmoWeather';
import { getCachedLifeData, saveMyCities, type SavedCity } from '../services/lifeDataService';

interface CityEntry {
  city: SavedCity;
  weather: AttractionWeather | null;
  loading: boolean;
  country: string;
}

// ── Nominatim country cache（session-level，不跨頁）──────────────────────────
const _countryCache = new Map<string, string>();

async function fetchCountry(lat: number, lng: number, lang: string): Promise<string> {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}:${lang}`;
  if (_countryCache.has(key)) return _countryCache.get(key)!;
  try {
    const acceptLang = lang.startsWith('en') ? 'en' : 'zh-TW,zh,en';
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${acceptLang}`,
      { headers: { 'User-Agent': 'ap-base-ee/life-center' } }
    );
    const json = await res.json();
    const country = json?.address?.country ?? (lang.startsWith('en') ? 'Unknown' : '未知');
    _countryCache.set(key, country);
    return country;
  } catch {
    return lang.startsWith('en') ? 'Unknown' : '未知';
  }
}

function getDayLabel(dateStr: string, index: number, lang: string): string {
  if (index === 0) return lang.startsWith('en') ? 'Today' : '今';
  const d = new Date(dateStr + 'T12:00:00');
  if (lang.startsWith('en')) return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
}

const MyCitiesPage: React.FC = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const isZh = !lang.startsWith('en');

  const [cities, setCities] = useState<SavedCity[]>(() => getCachedLifeData().my_cities);
  const [entries, setEntries] = useState<CityEntry[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const initial: CityEntry[] = cities.map(city => ({
      city,
      weather: null,
      loading: true,
      country: isZh ? '載入中...' : 'Loading...',
    }));
    setEntries(initial);

    cities.forEach((city, idx) => {
      // 並行取天氣 + 國家
      Promise.all([
        fetchAttractionWeather(city.lat, city.lng).catch(() => null),
        fetchCountry(city.lat, city.lng, lang),
      ]).then(([weather, country]) => {
        setEntries(prev => prev.map((e, i) =>
          i === idx ? { ...e, weather, loading: false, country } : e
        ));
        // 第一個城市載完後，展開它所在的國家小節
        setExpanded(prev => { const s = new Set(prev); s.add(country); return s; });
      });
    });
  }, [cities.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemove = useCallback(async (idx: number) => {
    const next = cities.filter((_, i) => i !== idx);
    setCities(next);
    try { await saveMyCities(next); }
    catch (e) { console.warn('[MyCities] save failed:', e); }
  }, [cities]);

  const toggleExpanded = (country: string) => {
    setExpanded(prev => {
      const next = new Set<string>(Array.from(prev));
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  };

  // 依國家分組（保持城市原本順序）
  const grouped = entries.reduce<Record<string, { entries: CityEntry[]; indices: number[] }>>(
    (acc, entry, idx) => {
      const key = entry.country;
      if (!acc[key]) acc[key] = { entries: [], indices: [] };
      acc[key].entries.push(entry);
      acc[key].indices.push(idx);
      return acc;
    },
    {}
  );
  const countryKeys = Object.keys(grouped);

  return (
    <div style={{ padding: '32px 40px', minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <LocationOnIcon sx={{ color: '#4285f4', fontSize: 28 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
          {isZh ? '我的城市' : 'My Cities'}
        </Typography>
        <Typography sx={{ color: '#757575', fontSize: 14, ml: 1 }}>
          ({cities.length})
        </Typography>
      </div>

      {/* Empty state */}
      {cities.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#9e9e9e' }}>
          <LocationOnIcon sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
          <Typography sx={{ fontSize: 16 }}>
            {isZh ? '尚未加入任何城市' : 'No cities saved yet'}
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 1, color: '#bdbdbd' }}>
            {isZh
              ? '請至「世界天氣」頁面，點選城市後按書籤加入'
              : 'Go to World Weather, click a city and press the bookmark button'}
          </Typography>
        </div>
      )}

      {/* Country sections */}
      {countryKeys.map(country => {
        const group = grouped[country];
        const isOpen = expanded.has(country);

        return (
          <Accordion
            key={country}
            expanded={isOpen}
            onChange={() => toggleExpanded(country)}
            disableGutters
            sx={{
              mb: 2,
              borderRadius: '12px !important',
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#1565c0' }} />}
              sx={{
                background: 'linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)',
                borderRadius: '12px',
                minHeight: 52,
                px: 2.5,
                '&.Mui-expanded': { minHeight: 52 },
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <LocationOnIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 700, color: '#1a237e', fontSize: 16 }}>
                  {country}
                </Typography>
                <Chip
                  label={group.entries.length}
                  size="small"
                  sx={{
                    background: '#1976d2',
                    color: '#fff',
                    fontWeight: 700,
                    height: 20,
                    fontSize: 12,
                    '.MuiChip-label': { px: 1 },
                  }}
                />
              </div>
            </AccordionSummary>

            <AccordionDetails sx={{ background: '#fafbfc', p: '16px 20px 20px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 20,
              }}>
                {group.entries.map((entry, localIdx) => (
                  <CityCard
                    key={`${entry.city.lat},${entry.city.lng}`}
                    entry={entry}
                    isZh={isZh}
                    lang={lang}
                    onRemove={() => handleRemove(group.indices[localIdx])}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

/* ── City Card ─────────────────────────────────────────────────────────────── */
interface CardProps {
  entry: CityEntry;
  isZh: boolean;
  lang: string;
  onRemove: () => void;
}

const CityCard: React.FC<CardProps> = ({ entry, isZh, lang, onRemove }) => {
  const { city, weather, loading } = entry;
  const name = isZh ? city.name : (city.nameEn || city.name);
  const today = weather?.forecast[0];
  const { label: weatherLabel } = today ? getWmoInfo(today.weatherCode, lang) : { label: '' };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Card header */}
      <div style={{
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
        padding: '16px 16px 12px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}>
        <div>
          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{name}</Typography>
          {loading ? (
            <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }} />
          ) : today ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span style={{ fontSize: 28 }}>{today.emoji}</span>
                <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
                  {today.tempMax}°
                </Typography>
                <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', alignSelf: 'flex-end', mb: 0.2 }}>
                  / {today.tempMin}°
                </Typography>
              </div>
              <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
                {weatherLabel}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
              {isZh ? '天氣資料無法取得' : 'Weather unavailable'}
            </Typography>
          )}
        </div>
        <Tooltip title={isZh ? '移除城市' : 'Remove city'}>
          <IconButton size="small" onClick={onRemove}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* 7-day forecast */}
      {!loading && weather && (
        <div style={{ padding: '12px 14px 14px' }}>
          <Typography sx={{ fontSize: 11, color: '#9e9e9e', mb: 1 }}>
            {isZh ? '7 天預報' : '7-Day Forecast'}
          </Typography>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            textAlign: 'center',
            gap: '2px 0',
          }}>
            {weather.forecast.map((d, i) => (
              <div key={`lbl-${d.date}`} style={{ fontSize: 10, color: '#9e9e9e', marginBottom: 2 }}>
                {getDayLabel(d.date, i, lang)}
              </div>
            ))}
            {weather.forecast.map(d => (
              <div key={`em-${d.date}`} style={{ fontSize: 16, lineHeight: '22px' }}>{d.emoji}</div>
            ))}
            {weather.forecast.map(d => (
              <div key={`hi-${d.date}`} style={{ fontSize: 11, color: '#1565c0', fontWeight: 600 }}>
                {d.tempMax}°
              </div>
            ))}
            {weather.forecast.map(d => (
              <div key={`lo-${d.date}`} style={{ fontSize: 11, color: '#9e9e9e' }}>
                {d.tempMin}°
              </div>
            ))}
            {weather.forecast.some(d => d.precipitation > 0) && weather.forecast.map(d => (
              <div key={`rn-${d.date}`} style={{ fontSize: 9, color: '#42a5f5' }}>
                {d.precipitation > 0 ? `${d.precipitation}mm` : ''}
              </div>
            ))}
          </div>

          <Divider sx={{ my: 1 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#757575' }}>
            <span>📍 {city.lat.toFixed(2)}°, {city.lng.toFixed(2)}°</span>
          </div>
        </div>
      )}

      {loading && (
        <div style={{ padding: '24px 0', display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} sx={{ color: '#4285f4' }} />
        </div>
      )}
    </div>
  );
};

export default MyCitiesPage;
